import { GA_MEASUREMENT_ID, IS_DEV } from './config'

declare global {
    interface Window {
        dataLayer: IArguments[]
        gtag: (...args: unknown[]) => void
    }
}

const SENSITIVE_QUERY_PARAMS = ['address', 'esiid', 'zipcode', 'city', 'state']

let initialized = false

function isLocalhost(): boolean {
    const { hostname } = window.location
    return hostname === 'localhost' || hostname === '127.0.0.1' || hostname === '[::1]'
}

function canTrack(): boolean {
    return initialized && !!GA_MEASUREMENT_ID && typeof window.gtag === 'function'
}

/** Strip PII from paths before sending to GA. */
export function sanitizePath(pathname: string, search: string): string {
    const params = new URLSearchParams(search.startsWith('?') ? search.slice(1) : search)
    SENSITIVE_QUERY_PARAMS.forEach((key) => params.delete(key))
    const query = params.toString()
    return query ? `${pathname}?${query}` : pathname
}

export function initGA(): void {
    if (initialized || !GA_MEASUREMENT_ID || isLocalhost()) {
        return
    }

    initialized = true

    const script = document.createElement('script')
    script.async = true
    script.src = `https://www.googletagmanager.com/gtag/js?id=${GA_MEASUREMENT_ID}`
    document.head.appendChild(script)

    window.dataLayer = window.dataLayer || []
    // gtag must push the Arguments object, matching Google's snippet
    window.gtag = function gtag() {
        // eslint-disable-next-line prefer-rest-params
        window.dataLayer.push(arguments)
    }

    window.gtag('js', new Date())
    window.gtag('config', GA_MEASUREMENT_ID, {
        send_page_view: false,
        ...(IS_DEV ? { debug_mode: true } : {}),
    })
}

export function trackPageView(path: string): void {
    if (!canTrack()) {
        return
    }

    window.gtag('event', 'page_view', {
        page_path: path,
        page_title: document.title,
        page_location: `${window.location.origin}${path}`,
    })
}

export function trackEvent(
    name: string,
    params?: Record<string, string | number | boolean | undefined>
): void {
    if (!canTrack()) {
        return
    }

    const cleaned: Record<string, string | number | boolean> = {}
    if (params) {
        Object.entries(params).forEach(([key, value]) => {
            if (value !== undefined && value !== '') {
                cleaned[key] = value
            }
        })
    }

    window.gtag('event', name, cleaned)
}
